import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { ChipGroup, Section } from "@/components/fields";
import { WORKING_STATUS, WORKING_WITH } from "@/constants";
import { getGps } from "@/lib/format";
import { toast } from "sonner";
import { CaretLeft } from "@phosphor-icons/react";

export default function StartDay() {
  const { user, setSession, loadSession } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [areas, setAreas] = useState([]);
  const [status, setStatus] = useState("field_work");
  const [withWhom, setWithWhom] = useState("alone");
  const [area, setArea] = useState("");
  const [km, setKm] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/areas").then(({ data }) => setAreas(data)); }, []);

  const fieldMode = ["field_work", "hq_work"].includes(status);

  const start = async () => {
    setLoading(true);
    const gps = await getGps();
    try {
      const { data } = await api.post("/session/start", {
        working_status: status, working_with: withWhom, working_area: area,
        start_km: km ? Number(km) : null, vehicle_type: vehicle, gps,
      });
      setSession(data); await loadSession();
      toast.success(t("day_started"));
      nav("/");
    } catch (e) { toast.error("Failed to start day"); }
    finally { setLoading(false); }
  };

  return (
    <Shell title={t("start_my_day")} nav={false}
      back={<button onClick={() => nav("/")} data-testid="back-btn"><CaretLeft size={22} /></button>}>
      <div className="space-y-4">
        <Section title={t("working_status")} testid="section-status">
          <ChipGroup options={WORKING_STATUS} value={status} onChange={setStatus} testid="working-status" />
        </Section>

        {fieldMode && (
          <>
            <Section title={t("working_with")} testid="section-with">
              <ChipGroup options={WORKING_WITH} value={withWhom} onChange={setWithWhom} testid="working-with" />
            </Section>
            <Section title={t("working_area")} testid="section-area">
              <select
                data-testid="area-select"
                value={area} onChange={(e) => setArea(e.target.value)}
                className="w-full min-h-[48px] rounded-lg border border-slate-200 px-3 bg-white"
              >
                <option value="">--</option>
                {(user?.working_areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
                {areas.filter((a) => !(user?.working_areas || []).includes(a.name)).map((a) => (
                  <option key={a.id} value={a.name}>{a.name}</option>
                ))}
              </select>
            </Section>
            <Section title={`${t("start_km")} · ${t("vehicle_type")}`} testid="section-optional">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="start-km" value={km} onChange={(e) => setKm(e.target.value)} inputMode="numeric" placeholder="KM" className="min-h-[48px] rounded-lg border border-slate-200 px-3" />
                <input data-testid="vehicle-type" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Bike / Car" className="min-h-[48px] rounded-lg border border-slate-200 px-3" />
              </div>
            </Section>
          </>
        )}

        <button
          onClick={start}
          disabled={loading || (fieldMode && !area)}
          data-testid="start-field-work"
          className="w-full min-h-[56px] rounded-xl bg-emerald-600 text-white font-head font-bold text-lg disabled:opacity-40 active:scale-[0.98]"
        >{t("start_field_work")}</button>
      </div>
    </Shell>
  );
}
